import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { messagingApi } from '@/api/messaging';
import { useSessionStore } from '@/state/sessionStore';

import { tNow } from '@/i18n/languageStore';

/** Query keys for community updates: what my connections shared, my own, and how many will see one from me. */
export const storyKeys = {
  all: ['stories'] as const,
  feed: ['stories'] as const,
  mine: ['stories', 'mine'] as const,
  audience: ['stories', 'audience'] as const,
};

const useSignedIn = () => useSessionStore((s) => s.status === 'signedIn');

/** What my connections shared in the last 24 hours (oldest first, so one person's story plays in order). */
export function useStoryFeed() {
  const enabled = useSignedIn();
  return useQuery({
    queryKey: storyKeys.feed,
    queryFn: messagingApi.getCommunityUpdateFeed,
    enabled,
  });
}

export function useMyStories() {
  const enabled = useSignedIn();
  return useQuery({
    queryKey: storyKeys.mine,
    queryFn: messagingApi.getMyCommunityUpdates,
    enabled,
  });
}

export function useStoryAudience() {
  const enabled = useSignedIn();
  return useQuery({
    queryKey: storyKeys.audience,
    queryFn: messagingApi.getCommunityUpdateAudience,
    enabled,
  });
}

export function useRemoveStory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updateId: string) => messagingApi.removeCommunityUpdate(updateId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

export function useHeartStory() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ updateId, hearted }: { updateId: string; hearted: boolean }) =>
      messagingApi.heartCommunityUpdate(updateId, hearted),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: storyKeys.all }),
  });
}

/** "Ends in 5h" / "Ends in 40m" / "Ended": how long an update stays up. */
export function expiryLabel(expiresAt: string, now: Date = new Date()): string {
  const minutes = Math.floor((new Date(expiresAt).getTime() - now.getTime()) / 60_000);
  if (!(minutes > 0)) return tNow('Ended');
  return minutes < 60
    ? tNow('Ends in {minutes}m', { minutes })
    : tNow('Ends in {floor}h', { floor: Math.floor(minutes / 60) });
}

/** "23h left" / "40m left": the same, for my own updates. */
export function timeLeft(expiresAt: string, now: Date = new Date()): string {
  const minutes = Math.floor((new Date(expiresAt).getTime() - now.getTime()) / 60_000);
  if (!(minutes > 0)) return 'ended';
  return minutes < 60
    ? tNow('{minutes}m left', { minutes })
    : tNow('{floor}h left', { floor: Math.floor(minutes / 60) });
}
